import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import type { BidType, MarketProgram } from '@/features/bidding/constants'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'
import {
  formatMarketClosingTimeAsString,
  formatRemainingTimeAsString,
  getSecondsToMarketClosingWarning,
  getStateOfMarket,
  hasIntradayBidding,
  MarketState,
} from '@/features/bidding/utils/getStateOfMarket'

export function useMarketClosingWarning() {
  const { t, i18n } = useTranslation()
  const { pushAlert, resetAlerts } = useAlertContext()

  const showMarketClosingWarning = useCallback(
    (bidDay: MarketDate, marketProgram: MarketProgram, bidType: BidType) => {
      if (hasIntradayBidding(marketProgram, bidType)) {
        return
      }

      const stateOfMarket = getStateOfMarket(bidDay, marketProgram, bidType)

      switch (stateOfMarket) {
        case MarketState.CLOSING:
          pushAlert({
            severity: 'warning',
            message: t('bidding.market_closed.preemptive_warning', {
              time: formatMarketClosingTimeAsString(marketProgram, i18n.language),
              timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram, i18n.language),
            }),
            isPersistent: false,
          })
          break
        case MarketState.CLOSED:
          pushAlert({
            severity: 'warning',
            message: t('bidding.market_closed.warning', {
              time: formatMarketClosingTimeAsString(marketProgram, i18n.language),
              timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram, i18n.language),
            }),
            isPersistent: false,
          })
          break
        case MarketState.OPEN:
          setTimeout(
            () => {
              pushAlert({
                severity: 'warning',
                message: t('bidding.market_closed.preemptive_warning', {
                  time: formatMarketClosingTimeAsString(marketProgram, i18n.language),
                  timeRemaining: formatRemainingTimeAsString(bidDay, marketProgram, i18n.language),
                }),
                isPersistent: false,
              })
            },
            getSecondsToMarketClosingWarning(bidDay, marketProgram) * 1000,
          )
          break
      }
    },
    [pushAlert, t, i18n.language],
  )

  const clearMarketClosingWarning = useCallback(() => {
    resetAlerts()
  }, [resetAlerts])

  return useMemo(
    () => ({
      showMarketClosingWarning,
      clearMarketClosingWarning,
    }),
    [showMarketClosingWarning, clearMarketClosingWarning],
  )
}
